import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { brandColors } from '@/design';
import type { TodoSwimLane } from '../../types';

/**
 * Workflowy-inspired daily date strip.
 *
 * Sits above the lane sections in Schedule grouping (works across List,
 * Kanban, and Bullets views). The big "Today, Mon May 4" heading and the
 * horizontal day strip mirror the Workflowy daily-note paradigm; clicking a
 * day jumps to the matching swim lane (`today` / `this_week` / `later`) and
 * expands it via the parent's `onJumpToLane` handler.
 *
 * The strip is a *navigation/index*, not a database filter — Flank's task
 * model uses lanes (+ optional `dueDate`) rather than per-day notes, so this
 * surface stays a soft index that nudges focus rather than changing what's
 * loaded.
 */

const DAY_SHORT_TWO = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const DAY_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const STRIP_DAYS = 21;
const STRIP_BACK_DEFAULT = 1;
const PAGE_SHIFT = 7;

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(a).getTime() - startOfDay(b).getTime()) / 86_400_000);
}

function dateToLane(d: Date, today: Date): TodoSwimLane {
  const diff = daysBetween(d, today);
  if (diff <= 0) return 'today';
  if (diff <= 6) return 'this_week';
  return 'later';
}

function relativePrefix(diffDays: number): string {
  if (diffDays === 0) return 'Today,';
  if (diffDays === 1) return 'Tomorrow,';
  if (diffDays === -1) return 'Yesterday,';
  if (diffDays > 1 && diffDays <= 6) return 'This week,';
  if (diffDays > 6) return 'Later,';
  return 'Past,';
}

export function ScheduleDateStrip({
  onJumpToLane,
  /** Read-only — surfaces the live lane mapping for the focused day. */
  selectedLane,
  /**
   * Fires whenever the user picks a day on the strip. Parent uses this to
   * swap in a "past day" view in place of the Today / This week / Later
   * lanes when a previous date is selected.
   */
  onSelectDate,
}: {
  onJumpToLane?: (lane: TodoSwimLane) => void;
  selectedLane?: (lane: TodoSwimLane) => void;
  onSelectDate?: (date: Date, info: { isPast: boolean; isToday: boolean; isFuture: boolean }) => void;
}) {
  // Refresh at midnight so the strip never shows a stale "Today" after being
  // left open overnight.
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;
    function scheduleNextRefresh() {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      timerId = setTimeout(() => {
        setNowTick(new Date());
        scheduleNextRefresh();
      }, tomorrow.getTime() - Date.now());
    }
    scheduleNextRefresh();
    return () => clearTimeout(timerId);
  }, []);
  const today = useMemo(() => startOfDay(nowTick), [nowTick]);

  const [selected, setSelected] = useState<Date>(today);
  const [stripStart, setStripStart] = useState<Date>(() => addDays(today, -STRIP_BACK_DEFAULT));
  const stripScrollRef = useRef<HTMLDivElement | null>(null);

  // When today rolls over (midnight tick), keep the selection on the new today
  // only if it was already pointing at what used to be today.
  const prevTodayRef = useRef<Date>(today);
  useEffect(() => {
    const prevToday = prevTodayRef.current;
    prevTodayRef.current = today;
    if (daysBetween(today, prevToday) === 0) return; // same day, no change
    setSelected((sel) => {
      if (daysBetween(sel, prevToday) === 0) {
        // Was on "old today" → move to new today
        return today;
      }
      return sel;
    });
    setStripStart(addDays(today, -STRIP_BACK_DEFAULT));
  }, [today]);

  // Keep the selected day visible whenever the strip window slides.
  useEffect(() => {
    const el = stripScrollRef.current;
    if (!el) return;
    const idx = daysBetween(selected, stripStart);
    if (idx < 0 || idx >= STRIP_DAYS) return;
    const child = el.children[idx] as HTMLElement | undefined;
    child?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [selected, stripStart]);

  const visibleDays = useMemo(
    () => Array.from({ length: STRIP_DAYS }, (_, i) => addDays(stripStart, i)),
    [stripStart],
  );

  const selectedDiff = daysBetween(selected, today);
  const headerPrefix = relativePrefix(selectedDiff);
  const headerDate = `${DAY_LONG[selected.getDay()]}, ${MONTH_SHORT[selected.getMonth()]} ${selected.getDate()}`;

  const selectDay = useCallback((d: Date) => {
    setSelected(d);
    const lane = dateToLane(d, today);
    const diff = daysBetween(d, today);
    onJumpToLane?.(lane);
    selectedLane?.(lane);
    onSelectDate?.(d, { isPast: diff < 0, isToday: diff === 0, isFuture: diff > 0 });
  }, [onJumpToLane, onSelectDate, selectedLane, today]);

  function jumpToToday() {
    setSelected(today);
    setStripStart(addDays(today, -STRIP_BACK_DEFAULT));
    onJumpToLane?.('today');
    onSelectDate?.(today, { isPast: false, isToday: true, isFuture: false });
  }

  // Navigate selected date one day backward.
  function goToPrevDay() {
    const prev = addDays(selected, -1);
    selectDay(prev);
    // Slide the window back if the new date would fall outside the left edge.
    setStripStart((s) => {
      if (daysBetween(prev, s) < 0) return addDays(s, -PAGE_SHIFT);
      return s;
    });
  }

  // Navigate selected date one day forward, capped at today.
  const isAtToday = daysBetween(selected, today) >= 0;
  function goToNextDay() {
    if (isAtToday) return;
    const next = addDays(selected, 1);
    selectDay(next);
    // Slide the window forward if the new date would fall outside the right edge.
    setStripStart((s) => {
      if (daysBetween(next, s) >= STRIP_DAYS) return addDays(s, PAGE_SHIFT);
      return s;
    });
  }

  return (
    <section
      aria-label="Schedule date strip"
      className="rounded-2xl px-4 pt-3 pb-3"
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div className="flex flex-wrap items-baseline gap-2 mb-2">
        <h2 className="text-lg font-bold text-[var(--color-text-primary)]">
          {headerPrefix}
        </h2>
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full"
          style={{
            background: selectedDiff === 0 ? `${brandColors.lightBlue}1F` : 'var(--color-surface-elevated)',
            color: selectedDiff === 0 ? brandColors.lightBlue : 'var(--color-text-secondary)',
            border: `1px solid ${selectedDiff === 0 ? `${brandColors.lightBlue}55` : 'var(--color-border)'}`,
          }}
        >
          {headerDate}
        </span>
        <span className="text-[11px] text-[var(--color-text-muted)] ml-auto">
          {selectedDiff === 0
            ? 'Today lane'
            : selectedDiff <= 6 && selectedDiff > 0
            ? 'This week lane'
            : selectedDiff > 6
            ? 'Later lane'
            : 'Past · Today lane'}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={goToPrevDay}
          className="shrink-0 p-1.5 rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-elevated)] transition-colors"
          aria-label="Previous day"
        >
          <ChevronLeft size={14} />
        </button>
        <button
          type="button"
          onClick={jumpToToday}
          className="shrink-0 p-1.5 rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-elevated)] transition-colors"
          aria-label="Jump to today"
          title="Jump to today"
        >
          <CalendarIcon size={14} />
        </button>

        <div
          ref={stripScrollRef}
          className="flex-1 flex gap-1 overflow-x-auto pb-0.5 -mx-1 px-1"
          style={{ scrollbarWidth: 'thin' }}
        >
          {visibleDays.map((d) => {
            const isToday = daysBetween(d, today) === 0;
            const isSelected = daysBetween(d, selected) === 0;
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            const isPast = daysBetween(d, today) < 0;
            const showsMonthChange = d.getDate() === 1;

            // Visual stack: Today gets a strong fill (Flank teal-blue) so it's
            // unmistakable; selected (non-today) gets a soft outlined chip;
            // weekends are dimmed; past days fade.
            const baseStyle: React.CSSProperties = {
              minWidth: 38,
              opacity: isPast && !isSelected ? 0.45 : 1,
            };

            let bg = 'transparent';
            let fg = 'var(--color-text-primary)';
            let border = '1px solid transparent';

            if (isToday) {
              bg = brandColors.lightBlue;
              fg = 'white';
              border = `1px solid ${brandColors.lightBlue}`;
            } else if (isSelected) {
              bg = `${brandColors.lightBlue}14`;
              fg = brandColors.lightBlue;
              border = `1px solid ${brandColors.lightBlue}55`;
            } else if (isWeekend) {
              fg = 'var(--color-text-muted)';
            }

            return (
              <button
                key={d.toISOString()}
                type="button"
                onClick={() => selectDay(d)}
                className="relative flex flex-col items-center justify-center px-1.5 py-1 rounded-lg transition-colors hover:bg-[var(--color-surface-elevated)]"
                style={{ ...baseStyle, background: bg, color: fg, border }}
                aria-pressed={isSelected}
                aria-label={`${DAY_LONG[d.getDay()]} ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`}
              >
                <span className="text-[9px] font-medium uppercase tracking-wider opacity-80">
                  {DAY_SHORT_TWO[d.getDay()]}
                </span>
                <span className="text-sm font-semibold leading-tight">{d.getDate()}</span>
                {showsMonthChange ? (
                  <span
                    className="absolute -top-2 left-1/2 -translate-x-1/2 text-[8px] font-semibold uppercase tracking-wider px-1 rounded"
                    style={{
                      background: 'var(--color-surface-elevated)',
                      color: 'var(--color-text-muted)',
                      border: '1px solid var(--color-border)',
                    }}
                  >
                    {MONTH_SHORT[d.getMonth()]}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={goToNextDay}
          disabled={isAtToday}
          className="shrink-0 p-1.5 rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-elevated)] transition-colors disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-[var(--color-text-muted)]"
          aria-label="Next day"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </section>
  );
}
